/**
 * Sound-effect helper — Phase 1B-I + 1B-K (2026-05-30).
 *
 * Three-tier asset lookup per sound:
 *
 *   1. **Locale-specific voice clip** (only for rec-start / rec-stop):
 *      `<locale>_recording_inprogress.mp3` / `<locale>_recording_stopped.mp3`
 *      Operator's current i18n language picks the file; falls through
 *      to English when the language isn't bundled.
 *   2. **Generic asset** at the canonical `/audio/<name>.mp3` path
 *      (the ffmpeg-generated chimes for tap / shutter / notify /
 *      rec-pause / rec-resume).
 *   3. **Web Audio synth fallback** — short envelope sine/noise tones
 *      shaped per intent (kept so dev/test without assets still has
 *      audible feedback).
 *
 * Lazy AudioContext: instantiated on first play(). Browsers require a
 * user gesture before audio plays — every code path that calls
 * playSound() runs from a button click or similar gesture so this is
 * safe.
 */
import i18next from "i18next";


export type SoundName =
  | "tap"
  | "shutter"
  | "notify"
  | "rec-start"
  | "rec-stop"
  | "rec-pause"
  | "rec-resume";


/** Generic / locale-agnostic asset paths. */
const ASSET_PATH: Record<SoundName, string> = {
  tap: "/audio/tap.mp3",
  shutter: "/audio/shutter.mp3",
  notify: "/audio/notify.mp3",
  "rec-start": "/audio/rec-start.mp3",
  "rec-stop": "/audio/rec-stop.mp3",
  "rec-pause": "/audio/rec-pause.mp3",
  "rec-resume": "/audio/rec-resume.mp3",
};


/** i18n locale → asset-filename prefix for voice clips. The bundled
 *  files use ISO codes directly; this map is the explicit allowlist
 *  of supported locales (unbundled languages fall through to English
 *  in candidatePaths()). */
const VOICE_LOCALE_PREFIX: Record<string, string> = {
  cs: "cs",
  da: "da",
  de: "de",
  el: "el",
  en: "en",
  es: "es",
  fi: "fi",
  fr: "fr",
  it: "it",
  nl: "nl",
  no: "no",
  pl: "pl",
  pt: "pt",
  ro: "ro",
  sv: "sv",
};


/** Build the candidate-path list a given SoundName should try, in
 *  preference order. Voice-bearing sounds prepend the locale variant. */
export function candidatePaths(name: SoundName): string[] {
  if (name === "rec-start" || name === "rec-stop") {
    const raw =
      (typeof i18next !== "undefined" && i18next.language) || "en";
    // Normalise "en-US" → "en"; default to "en" when unbundled.
    const base = (raw.split("-")[0] ?? "en").toLowerCase();
    const prefix = VOICE_LOCALE_PREFIX[base] ?? "en";
    const suffix =
      name === "rec-start" ? "recording_inprogress" : "recording_stopped";
    const localePath = `/audio/${prefix}_${suffix}.mp3`;
    // Always include the English voice as the secondary, then the
    // generic `rec-start.mp3` fallback (mainly for tests / dev that
    // never bundled the voice tier).
    return Array.from(
      new Set([
        localePath,
        `/audio/en_${suffix}.mp3`,
        ASSET_PATH[name],
      ]),
    );
  }
  return [ASSET_PATH[name]];
}


let ctx: AudioContext | null = null;
// Cache for fetched buffers so we don't re-fetch on every play. null =
// file confirmed missing → fall through to synth path next time.
const bufferCache = new Map<string, AudioBuffer | null>();


function getCtx(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const Ctor =
      (window.AudioContext as typeof AudioContext | undefined) ??
      ((window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext);
    if (!Ctor) return null;
    ctx = new Ctor();
    return ctx;
  } catch {
    return null;
  }
}


async function tryLoadOnePath(path: string): Promise<AudioBuffer | null> {
  if (bufferCache.has(path)) return bufferCache.get(path) ?? null;
  const audioCtx = getCtx();
  if (!audioCtx) return null;
  try {
    const res = await fetch(path);
    if (!res.ok) {
      bufferCache.set(path, null);
      return null;
    }
    const arr = await res.arrayBuffer();
    const buf = await audioCtx.decodeAudioData(arr);
    bufferCache.set(path, buf);
    return buf;
  } catch {
    bufferCache.set(path, null);
    return null;
  }
}


async function tryLoadAsset(
  name: SoundName,
): Promise<AudioBuffer | null> {
  for (const path of candidatePaths(name)) {
    const buf = await tryLoadOnePath(path);
    if (buf) return buf;
  }
  return null;
}


function playSynth(name: SoundName) {
  const audioCtx = getCtx();
  if (!audioCtx) return;
  // Cheap synth fallback — short envelope around a sine tone. Different
  // pitches per intent so the user can distinguish them.
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  const now = audioCtx.currentTime;
  let freq = 880;
  let dur = 0.1;
  let peak = 0.15;
  switch (name) {
    case "tap":
      freq = 1400;
      dur = 0.05;
      peak = 0.08;
      break;
    case "shutter":
      freq = 220;
      dur = 0.08;
      peak = 0.25;
      break;
    case "notify":
      freq = 988; // ~B5
      dur = 0.18;
      peak = 0.18;
      break;
    case "rec-start":
      freq = 660;
      dur = 0.3;
      peak = 0.22;
      break;
    case "rec-stop":
      freq = 440;
      dur = 0.3;
      peak = 0.22;
      break;
    case "rec-pause":
      // Two-tone descending — "going to sleep".
      freq = 587; // D5
      dur = 0.18;
      peak = 0.2;
      break;
    case "rec-resume":
      // Two-tone ascending — "waking up".
      freq = 784; // G5
      dur = 0.18;
      peak = 0.2;
      break;
  }
  osc.frequency.value = freq;
  osc.type = name === "shutter" ? "square" : "sine";
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(peak, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
  osc.connect(gain).connect(audioCtx.destination);
  osc.start(now);
  osc.stop(now + dur + 0.05);
}


/** Fire-and-forget — never throws. Tries real asset first, falls back
 * to synth. Safe to call without awaiting. */
export async function playSound(name: SoundName): Promise<void> {
  const audioCtx = getCtx();
  if (!audioCtx) return;
  if (audioCtx.state === "suspended") {
    try {
      await audioCtx.resume();
    } catch {
      // ignore
    }
  }
  const buf = await tryLoadAsset(name);
  if (buf) {
    const src = audioCtx.createBufferSource();
    src.buffer = buf;
    src.connect(audioCtx.destination);
    src.start();
    return;
  }
  playSynth(name);
}
